import React from 'react'
import { useTranslation } from 'react-i18next'

import { FormikErrors, useFormik } from 'formik'
import produce from 'immer'

import { useToastContext } from '../../../../../../contexts'
import { getHandleableError } from '../../../../../../utils/errors/isXiaError'
import {
  FieldToValidate,
  validateFormikFields,
} from '../../../../../../utils/helper/validateFormikFields'
import { emailRegex } from '../../../../../../utils/regex'
import {
  filterRoleSetsByActiveFeatures,
  useSupplierFeatures,
} from '../../../../../authorization'
import { mutatePermissions } from '../../../../../authorization/data/mutatePermissions'
import { useConfigContext } from '../../../../../ui-config'
import {
  RoleAssignmentDto,
  useAllRoleSets,
  useAllRoles,
} from '../../../../roleAssignments/data'
import { requestPutRoleAssignmentById } from '../../../../roleAssignments/data/api'
import { useAllSuppliers } from '../../../../suppliers/data'
import { useMemberById } from '../../../data'
import { EditAssignee } from './EditAssignee'

interface FormValues extends RoleAssignmentDto {}
export type FormFieldName = keyof FormValues

type Props = {
  id: string
  setIsRequestRunning: (params: boolean) => void
  visible: boolean
  onHide: () => void
  mutate: () => void
}
export const EditAssigneeContainer: React.FC<Props> = ({
  id,
  setIsRequestRunning,
  onHide,
  visible,
  mutate,
}) => {
  const { t } = useTranslation()
  const { data: roleAssignment } = useMemberById(id)
  const { data: roleSets } = useAllRoleSets()
  const { data: suppliers } = useAllSuppliers()
  const { data: allRoles } = useAllRoles()
  const supplierFeatures = useSupplierFeatures()
  const filteredRoleSets = filterRoleSetsByActiveFeatures(
    roleSets,
    supplierFeatures,
  )

  const { uiConfig: config } = useConfigContext()
  const { showToast } = useToastContext()

  const projectRoles =
    allRoles?.filter(role => role.isProjectScope === true).map(r => r.name) ||
    []

  // formik
  const formik = useFormik<FormValues>({
    enableReinitialize: true,
    initialValues: {
      role: roleAssignment?.role || '',
      assignee: roleAssignment?.assignee || '',
      project: roleAssignment?.project || '',
    },
    validate: data => {
      let errors: FormikErrors<FormValues> = {}

      const fieldsToValidate: FieldToValidate[] = [
        {
          fieldName: 'assignee',
          config: config.selfservices.members.editAssignee.assignee,
        },
        {
          fieldName: 'project',
          config: config.selfservices.members.editAssignee.project,
        },
        {
          fieldName: 'role',
          config: config.selfservices.members.editAssignee.role,
        },
      ]

      if (!emailRegex.test(data.assignee as string))
        errors.assignee = 'Must be a valid email address.'

      validateFormikFields(fieldsToValidate, data, errors)

      return errors
    },
    onSubmit: async data => {
      try {
        onHide()
        setIsRequestRunning(true)
        formik.resetForm()
        if (!projectRoles.includes(data.role)) {
          data.project = ''
        }
        await submitForm(data)

        showToast({
          style: {},
          closable: true,
          severity: 'success',
          summary: t('members.table.edit.dialog.save.messages.success.summary'),
          detail: t('members.table.edit.dialog.save.messages.success.detail', {
            email: data.assignee,
          }),
        })
      } catch (error) {
        let handleableError = getHandleableError(error)
        let errorMessage = handleableError.message

        // show error toast
        showToast({
          style: {},
          closable: true,
          severity: 'error',
          sticky: true,
          summary: t('members.table.edit.dialog.save.messages.failure.summary'),
          detail: errorMessage,
        })
      } finally {
        setIsRequestRunning(false)
        mutate()
        mutatePermissions()
      }
    },
  })

  const submitForm = async (formData: FormValues) => {
    const newData: RoleAssignmentDto = produce(roleAssignment!, draft => {
      draft.assignee = formData.assignee
      draft.project = formData.project
      draft.role = formData.role
    })
    await requestPutRoleAssignmentById(id, newData)
  }

  const isFormFieldInvalid = (name: FormFieldName) =>
    !!(formik.touched[name] && formik.errors[name])

  const getFormErrorMessage = (name: FormFieldName) => {
    return isFormFieldInvalid(name) ? (
      <small className="p-error">{formik.errors[name]}</small>
    ) : (
      <small className="p-error">&nbsp;</small>
    )
  }

  return (
    <EditAssignee
      formik={formik}
      isFormFieldInvalid={isFormFieldInvalid}
      getFormErrorMessage={getFormErrorMessage}
      config={config.selfservices.members.editAssignee}
      roleSets={filteredRoleSets}
      suppliers={suppliers}
      projectRoles={projectRoles}
      onHide={onHide}
      visible={visible}
    />
  )
}
